import React, { useState } from "react";
import { getDisplayPlatforms } from "../utils/networkNames";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { ExternalLink, ChevronDown, ChevronRight, Share2, Heart, Play } from "lucide-react";
import Typography from "./ui/Typography";
import Button from "./ui/Button";
import Chip from "./ui/Chip";
import RatingTooltipBadge from "./ui/RatingTooltipBadge";
import SeasonBadge from "./ui/SeasonBadge";
import PosterPlaceholder from "./ui/PosterPlaceholder";
import SectionTitle from "./ui/SectionTitle";
import DiscoveryCue from "./DiscoveryCue";
import { cn } from "../utils/classNames";
import { formatReleaseDate } from "../utils/formatReleaseDate";
import { BUTTON_LABELS } from "../constants/buttonLabels";
import { shareShow } from "../utils/share";
import { handleInternalLinkClick } from "../utils/navigation";
import ShowsLikeLink from "./ShowsLikeLink";
import { getContentRating, normalizeSeasonNumber } from "../utils/showFields";
import { getPrimaryRating } from "../utils/showRatings";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";
import { useTextTruncation } from "../hooks/useTextTruncation";
import { trackEvent } from "../utils/analytics";
import { getWatchlistToggleLabel } from "../utils/watchlist";

const ShowListItem = ({ release, density, highlighted = false, isInWatchlist, onToggleWatchlist }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [failedPosterSrc, setFailedPosterSrc] = useState(null);
  const [heartPulse, setHeartPulse] = useState(false);
  const inWatchlist = isInWatchlist?.(release);
  const contentRating = getContentRating(release);
  const primaryRating = getPrimaryRating(release);
  const trailerUrl = release.trailers?.find((trailer) => trailer?.url)?.url || null;

  // Get primary network
  const primaryNetwork = getDisplayPlatforms(release);

  // Get display genres (limit to first 3)
  const displayGenres = release.genres?.slice(0, 3) || [];

  // Description text
  const description = release.plotOverview?.trim() || "No summary available.";
  const hasDescription = description !== "No summary available.";
  const posterSrc = release.poster || null;
  const posterFailed = posterSrc && failedPosterSrc === posterSrc;
  const { ref: summaryRef, isTruncated: isSummaryTruncated } = useTextTruncation(
    [description, density],
    { disabled: isExpanded || !hasDescription }
  );
  const metadataItems = [
    primaryRating !== null ? (
      <RatingTooltipBadge key="rating" show={release} tooltipClassName="max-w-sm" />
    ) : null,
    normalizeSeasonNumber(release.seasonNumber) !== null ? (
      <SeasonBadge key="season" seasonNumber={release.seasonNumber} part={release.seasonPart} />
    ) : null,
    <Typography key="date" variant="caption" className="text-label-light">
      {formatReleaseDate(release.releaseDate).toUpperCase()}
    </Typography>,
    contentRating ? (
      <Chip key="content-rating" variant="outline" size="badge" className="text-label">
        {contentRating}
      </Chip>
    ) : null,
    release.episodeCount ? (
      <Chip key="episodes" variant="outline" size="badge" className="text-label">
        {release.episodeCount} eps
      </Chip>
    ) : null,
    primaryNetwork ? (
      <Typography key="network" variant="caption" className="text-label">
        {primaryNetwork.toUpperCase()}
      </Typography>
    ) : null,
  ].filter(Boolean);

  return (
    <motion.div
      id={`show-${release.id}`}
      className={cn(
        "bg-card text-card-foreground border border-accent-gold/70 rounded-xl",
        "transition-all duration-200",
        isExpanded && "border-primary",
        highlighted && "random-pick-highlight"
      )}
      initial={{ y: 0, boxShadow: "none" }}
      animate={
        isHovered
          ? { y: -6, boxShadow: "var(--shadow-card-dense-hover)" }
          : { y: 0, boxShadow: "var(--shadow-card-base)" }
      }
      transition={{
        duration: 0.25,
        ease: [0.0, 0.0, 0.2, 1],
        boxShadow: { duration: 0.25, ease: [0.0, 0.0, 0.2, 1] },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <div
        className={cn(
          "flex gap-4 relative",
          density === "compact" ? "p-3" : "p-4"
        )}
      >
        {/* Poster Thumbnail */}
        <div className={cn(
          "shrink-0 border border-accent-gold/60 overflow-hidden rounded-md",
          density === "compact" ? "w-[60px] sm:w-[80px] h-[90px] sm:h-[120px]" : "w-[80px] sm:w-[100px] h-[120px] sm:h-[150px]"
        )}>
          {posterSrc && !posterFailed ? (
            <img
              src={posterSrc}
              alt={`${release.title} poster`}
              className="w-full h-full object-cover grayscale-[20%] contrast-110"
              loading="lazy"
              onError={() => setFailedPosterSrc(posterSrc)}
            />
          ) : (
            <PosterPlaceholder
              showId={release.id}
              className="w-full h-full bg-poster-placeholder-gradient"
            />
          )}
        </div>

        {/* Content */}
        <div className={cn(
          "flex-1 min-w-0 flex flex-col pr-16 sm:pr-28 pb-12",
          density === "compact" ? "gap-1.5" : "gap-2"
        )}>
          {/* Title */}
          <Typography
            as="h3" variant="h4"
            className={cn(
              "line-clamp-2 leading-tight border-b border-dashed border-accent-gold/60 pb-2 font-display",
              density === "compact" && "text-body"
            )}
          >
            {release.slug ? (
              <a href={`/show/${release.slug}`} onClick={(e) => handleInternalLinkClick(e, `/show/${release.slug}`)} className="title-link">
                {release.title}
              </a>
            ) : release.title}
          </Typography>

          <ShowsLikeLink show={release} surface="show_list" className="self-start" />

          {/* Meta Info Row */}
          <div className="flex flex-wrap items-center gap-2 text-body-small text-muted-foreground">
            {metadataItems.map((item, index) => (
              <React.Fragment key={item.key}>
                {index > 0 ? <span className="text-body-xs font-bold" aria-hidden="true">|</span> : null}
                {item}
              </React.Fragment>
            ))}
          </div>


          {/* Genres */}
          <div className="flex items-center gap-2 flex-wrap">
            {displayGenres.length > 0 && (
              <>
                {displayGenres.slice(0, density === "compact" ? 2 : 3).map((genre, index) => (
                  <Chip key={index} variant="outline">
                    {genre}
                  </Chip>
                ))}
              </>
            )}
          </div>

          <DiscoveryCue
            cue={release.discoveryCue}
            currentSlug={release.slug || ""}
            currentTitle={release.title || ""}
            surface="show_list"
            compact={density === "compact"}
          />

          {hasDescription && (
            <div className="space-y-2">
              <Typography
                ref={summaryRef}
                variant="body-small"
                className={cn(
                  "text-muted-foreground leading-relaxed",
                  isExpanded ? "pt-2 border-t border-border/30" : "line-clamp-2"
                )}
              >
                {description}
              </Typography>
              {isSummaryTruncated && (
                <button
                  type="button"
                  onClick={() => setIsExpanded((current) => !current)}
                  className="flex items-center gap-1 text-body-xs text-muted-foreground hover:text-primary transition-colors text-label-light"
                  aria-label={isExpanded ? "Collapse summary" : "Expand summary"}
                  aria-expanded={isExpanded}
                >
                  {isExpanded ? (
                    <>
                      <ChevronDown className="h-3 w-3" />
                      <span>Hide summary</span>
                    </>
                  ) : (
                    <>
                      <ChevronRight className="h-3 w-3" />
                      <span>Read summary</span>
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Actions - Bottom Right */}
        <div className="absolute bottom-4 right-4 flex items-center gap-2">
          <Button
            type="button"
            variant="icon-dark"
            size="icon"
            className={cn(
              "h-9 w-9 sm:h-10 sm:w-10 opacity-85",
              inWatchlist && "border-primary bg-primary text-primary-foreground opacity-100 hover:bg-primary"
            )}
            onClick={() => {
              onToggleWatchlist?.(release);
              setHeartPulse(true);
              setTimeout(() => setHeartPulse(false), 300);
            }}
            aria-label={getWatchlistToggleLabel(release.title)}
            aria-pressed={Boolean(inWatchlist)}
          >
            <Heart className={cn("h-4 w-4", inWatchlist && "fill-current text-primary-foreground", heartPulse && "watchlist-pulse")} aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="icon-dark"
            size="icon"
            className="h-9 w-9 sm:h-10 sm:w-10 opacity-85"
            onClick={() => shareShow(release)}
            aria-label={`Share ${release.title}`}
          >
            <Share2 className="h-4 w-4" aria-hidden="true" />
          </Button>
          {trailerUrl && (
            <Button
              variant="wireframe"
              size="sm"
              className="shrink-0 text-label-light rounded-md"
              onClick={() => {
                trackEvent("outbound_click", { surface: "show_list", type: "trailer", slug: release.slug });
                window.open(trailerUrl, '_blank', 'noopener,noreferrer');
              }}
            >
              <Play className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{BUTTON_LABELS.WATCH_TRAILER}</span>
            </Button>
          )}
          {release.detailsUrl && (
            <Button
              variant="space"
              size="sm"
              className="shrink-0 text-label-light rounded-md"
              onClick={() => {
                trackEvent("outbound_click", { surface: "show_list", type: "details", slug: release.slug });
                window.open(release.detailsUrl, '_blank', 'noopener,noreferrer');
              }}
            >
              <ExternalLink className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{BUTTON_LABELS.QUERY_IMDB}</span>
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

ShowListItem.propTypes = {
  release: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    rating: PropTypes.number,
    poster: PropTypes.string,
    seasonNumber: PropTypes.number,
    seasonPart: PropTypes.string,
    releaseDate: PropTypes.string.isRequired,
    sourceName: PropTypes.string,
    genres: PropTypes.arrayOf(PropTypes.string),
    networks: PropTypes.arrayOf(PropTypes.string),
    plotOverview: PropTypes.string,
    contentRating: PropTypes.string,
    episodeCount: PropTypes.number,
    trailers: PropTypes.arrayOf(PropTypes.shape({
      url: PropTypes.string,
      name: PropTypes.string,
    })),
    detailsUrl: PropTypes.string,
    slug: PropTypes.string,
    discoveryCue: PropTypes.shape({
      type: PropTypes.oneOf(["inbound", "taste"]),
      label: PropTypes.string,
      anchor: PropTypes.shape({
        slug: PropTypes.string,
        title: PropTypes.string,
      }),
      reasons: PropTypes.arrayOf(PropTypes.string),
    }),
  }).isRequired,
  density: PropTypes.oneOf(["comfortable", "compact"]),
  highlighted: PropTypes.bool,
  isInWatchlist: PropTypes.func,
  onToggleWatchlist: PropTypes.func,
};

const NewShowsList = React.forwardRef(
  ({
    releases,
    loading = false,
    className,
    showTitle = true,
    title = "Upcoming & New Seasons",
    density = "comfortable",
    highlightedShowId = null,
    isInWatchlist,
    onToggleWatchlist,
    ...props
  }, ref) => {
    const prefersReducedMotion = usePrefersReducedMotion();
    const getEntryProps = (index) => ({
      initial: { opacity: prefersReducedMotion ? 1 : 0, y: prefersReducedMotion ? 0 : 12 },
      animate: { opacity: 1, y: 0 },
      transition: {
        duration: 0.25,
        ease: [0.0, 0.0, 0.2, 1],
        delay: prefersReducedMotion ? 0 : index * 0.05,
      },
    });

    if (loading) {
      return (
        <div className={cn("space-y-4", className)} ref={ref} {...props}>
          {showTitle && (
            <SectionTitle variant="h2">
              {title}
            </SectionTitle>
          )}

          {/* Loading skeleton */}
          <div className="space-y-3">
            {Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="bg-card rounded-xl border border-accent-gold/70 overflow-hidden animate-pulse"
              >
                <div className="flex gap-4 p-4">
                  <div className="w-[100px] h-[150px] bg-muted rounded" />
                  <div className="flex-1 space-y-3">
                    <div className="h-5 bg-muted rounded w-3/4" />
                    <div className="h-4 bg-muted rounded w-1/2" />
                    <div className="flex gap-2">
                      <div className="h-6 bg-muted rounded-full w-16" />
                      <div className="h-6 bg-muted rounded-full w-16" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (!releases || releases.length === 0) {
      return (
        <div className={cn("space-y-6", className)} ref={ref} {...props}>
          {showTitle && (
            <SectionTitle variant="h2">
              {title}
            </SectionTitle>
          )}

          <div className="text-center py-12">
            <div className="text-muted-foreground mb-4">
              <svg
                className="mx-auto h-12 w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h3 className="text-body font-medium text-foreground mb-2">
              No shows found
            </h3>
            <p className="text-body-small text-muted-foreground max-w-sm mx-auto">
              Try adjusting your filters or check back later for new releases.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className={cn("space-y-4", className)} ref={ref} {...props}>
        {showTitle && (
          <SectionTitle variant="h2">
            {title}
          </SectionTitle>
        )}

        <div className="space-y-3">
          {releases.map((release, index) => (
            <motion.div key={release.id} {...getEntryProps(index)}>
              <ShowListItem
                release={release}
                density={density}
                highlighted={String(release.id) === highlightedShowId}
                isInWatchlist={isInWatchlist}
                onToggleWatchlist={onToggleWatchlist}
              />
            </motion.div>
          ))}
        </div>
      </div>
    );
  }
);

NewShowsList.displayName = "NewShowsList";

NewShowsList.propTypes = {
  releases: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      rating: PropTypes.number,
      poster: PropTypes.string,
      seasonNumber: PropTypes.number,
      releaseDate: PropTypes.string.isRequired,
      sourceName: PropTypes.string,
      genres: PropTypes.arrayOf(PropTypes.string),
      networks: PropTypes.arrayOf(PropTypes.string),
      plotOverview: PropTypes.string,
      episodeCount: PropTypes.number,
      detailsUrl: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  className: PropTypes.string,
  showTitle: PropTypes.bool,
  title: PropTypes.string,
  density: PropTypes.oneOf(["comfortable", "compact"]),
  highlightedShowId: PropTypes.string,
  isInWatchlist: PropTypes.func,
  onToggleWatchlist: PropTypes.func,
};

export default NewShowsList;
